import { Alert, Button, EmptyState, PageHeader, Skeleton } from "@emdur/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ProposalReview } from "../components/ProposalReview";
import { useRegistry } from "../components/RegistryProvider";
import { type Proposal, proposalHref } from "../lib/proposals";
export function Approvals() {
  const registry = useRegistry();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(0);
  const [keyboardSelecting, setKeyboardSelecting] = useState(false);
  const [leaving, setLeaving] = useState<string[]>([]);
  const [decided, setDecided] = useState<string[]>([]);
  const [completing, setCompleting] = useState<Proposal[]>([]);
  const sequence = useRef(0);
  const [command, setCommand] = useState<{
    id: string;
    action: "approve" | "decline";
    sequence: number;
  }>();
  // Keep a confirmed card through the mark, even if a background refresh
  // already observes that it is no longer pending on the server.
  const cards = [
    ...new Map(
      [...registry.proposals, ...completing].map((proposal) => [
        proposal.id,
        proposal,
      ]),
    ).values(),
  ].filter((proposal) => !decided.includes(proposal.id));
  useEffect(() => {
    setSelected((before) => Math.min(before, Math.max(0, cards.length - 1)));
  }, [cards.length]);
  useEffect(() => {
    function key(event: KeyboardEvent) {
      const target = event.target as HTMLElement;
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.isComposing ||
        target.closest(
          "input, textarea, select, [contenteditable=true], [role=dialog], [role=alertdialog]",
        )
      )
        return;
      const current = cards[selected];
      if (!current) return;
      const letter = event.key.toLowerCase();
      if (letter === "j" || letter === "k") {
        event.preventDefault();
        setKeyboardSelecting(true);
        const next = Math.max(
          0,
          Math.min(cards.length - 1, selected + (letter === "j" ? 1 : -1)),
        );
        setSelected(next);
        document
          .querySelector<HTMLElement>(`[data-proposal="${cards[next]!.id}"] a`)
          ?.focus();
      } else if (letter === "a" || letter === "d") {
        event.preventDefault();
        setCommand({
          id: current.id,
          action: letter === "a" ? "approve" : "decline",
          sequence: ++sequence.current,
        });
      } else if (event.key === "Enter" && target === document.body) {
        event.preventDefault();
        void navigate(proposalHref(current.organization_id, current.id));
      }
    }
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [cards, selected, navigate]);
  function decision(proposal: Proposal) {
    setLeaving((ids) => [...ids, proposal.id]);
    window.setTimeout(
      () => {
        setDecided((ids) => [...ids, proposal.id]);
        setCompleting((items) =>
          items.filter((item) => item.id !== proposal.id),
        );
        void registry.reload();
      },
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? 0 : 180,
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Approvals"
        description="Review changes proposed by agents across the organisations you administer."
      />
      <p className="text-sm text-muted">
        J / K move · A approve · D decline · Enter opens a proposal
      </p>
      {registry.error && (
        <Alert tone="danger" title="Approvals could not be loaded">
          {registry.error}
          <p>Try again to see the latest changes awaiting your decision.</p>
          <Button onPress={() => void registry.reload()}>Try again</Button>
        </Alert>
      )}
      {registry.loading ? (
        <Skeleton label="Loading approvals" />
      ) : !registry.error && cards.length === 0 ? (
        <EmptyState title="You're all caught up">
          When an agent needs your approval, its proposed change appears here
          with the reasons and the time left to decide.
        </EmptyState>
      ) : (
        <div
          className="flex flex-col gap-5"
          onPointerDownCapture={() => setKeyboardSelecting(false)}
        >
          {cards.map((proposal, index) => (
            <div
              key={proposal.id}
              data-proposal={proposal.id}
              data-current={
                (keyboardSelecting && selected === index) || undefined
              }
              data-leaving={leaving.includes(proposal.id) || undefined}
              className="proposal-card @container"
            >
              <ProposalReview
                proposal={proposal}
                autoVerify
                onConfirmed={(confirmed) =>
                  setCompleting((items) => [
                    ...items.filter((item) => item.id !== confirmed.id),
                    confirmed,
                  ])
                }
                onDecided={decision}
                command={command?.id === proposal.id ? command : undefined}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
